import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const PRESETS = {
  fast: { start: 25500, end: 26000 },
  middle: { start: 25500, end: 30000 },
  full: { start: 1024, end: 65535 }
};

const RconScannerSettings = ({
  portStart,
  setPortStart,
  portEnd,
  setPortEnd,
  threads,
  setThreads,
  selectedDict,
  setSelectedDict,
  isMutator,
  setIsMutator,
  isRunning,
  onToggleAudit,
  currentStage,
  availableWordlists = [],
  onPresetChange
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const getActivePreset = (start, end) => {
    const s = Number(start);
    const e = Number(end);
    if (s === PRESETS.fast.start && e === PRESETS.fast.end) return 'fast';
    if (s === PRESETS.middle.start && e === PRESETS.middle.end) return 'middle';
    if (s === PRESETS.full.start && e === PRESETS.full.end) return 'full';
    return null;
  };

  const activePreset = getActivePreset(portStart, portEnd);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleApplyPreset = useCallback((type) => {
    const preset = PRESETS[type];
    if (!preset) return;
    setPortStart(preset.start);
    setPortEnd(preset.end);
    onPresetChange?.(type, preset);
  }, [setPortStart, setPortEnd, onPresetChange]);

  const handleSelectDict = useCallback((dict) => {
    if (!dict.id) return;
    setSelectedDict(dict.id);
    try {
      localStorage.setItem('rcon_selected_dict', dict.id);
    } catch (e) {}
    setIsDropdownOpen(false);
  }, [setSelectedDict]);

  // Load actual wordlists/bases from backend or fallback to default bases
  const allDicts = React.useMemo(() => {
    const list = (availableWordlists && availableWordlists.length > 0)
      ? availableWordlists
      : ['base1', 'base2'];

    return list.map((w, idx) => ({
      id: w,
      titleRu: w,
      titleEn: w,
      subRu: 'Словарь паролей',
      subEn: isEn ? 'Wordlist' : 'Словарь паролей',
      badge: idx === 0 ? (isEn ? 'RECOMMENDED' : 'РЕКОМЕНДОВАНО') : '',
      badgeEn: 'RECOMMENDED'
    }));
  }, [availableWordlists, isEn]);

  // Synchronize selectedDict when bases arrive
  useEffect(() => {
    const list = (availableWordlists && availableWordlists.length > 0)
      ? availableWordlists
      : ['base1', 'base2'];

    if (!selectedDict || !list.includes(selectedDict)) {
      setSelectedDict(list[0]);
    }
  }, [availableWordlists, selectedDict, setSelectedDict]);

  const currentDictObj = allDicts.find(d => d.id === selectedDict) || allDicts[0];

  return (
    <div className="rcon-card">
      <div className="card-heading">
        <div className="card-title-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="m10 15 5-3-5-3v6Z" />
          </svg>
          <h3 className="card-title">
            {isEn ? 'Scanner Settings' : 'Настройки сканера'}
          </h3>
        </div>
      </div>

      {/* Port Range with Presets */}
      <div className="field-group" style={{ marginBottom: '18px' }}>
        <label className="field-label">
          {isEn ? 'PORT SCAN RANGE' : 'Диапазон сканирования портов'}
        </label>
        <div className="port-range-row">
          <div className="field-input-wrapper">
            <input
              type="number"
              className="field-input"
              value={portStart}
              onChange={(e) => {
                setPortStart(e.target.value === '' ? '' : Number(e.target.value));
              }}
              min="1"
              max="65535"
              disabled={isRunning}
            />
          </div>
          <span className="port-dash">—</span>
          <div className="field-input-wrapper">
            <input
              type="number"
              className="field-input"
              value={portEnd}
              onChange={(e) => {
                setPortEnd(e.target.value === '' ? '' : Number(e.target.value));
              }}
              min="1"
              max="65535"
              disabled={isRunning}
            />
          </div>
        </div>

        <div className="presets-buttons-wrap">
          <button
            type="button"
            className={`preset-chip-btn ${activePreset === 'fast' ? 'active' : ''}`}
            onClick={() => handleApplyPreset('fast')}
            disabled={isRunning}
          >
            Fast
          </button>
          <button
            type="button"
            className={`preset-chip-btn ${activePreset === 'middle' ? 'active' : ''}`}
            onClick={() => handleApplyPreset('middle')}
            disabled={isRunning}
          >
            Middle
          </button>
          <button
            type="button"
            className={`preset-chip-btn ${activePreset === 'full' ? 'active' : ''}`}
            onClick={() => handleApplyPreset('full')}
            disabled={isRunning}
          >
            Full
          </button>
        </div>
      </div>

      {/* Threads execution */}
      <div className="field-group" style={{ marginBottom: '18px' }}>
        <div className="threads-header-row">
          <span className="field-label">
            {isEn ? 'EXECUTION THREADS' : 'Потоки выполнения'}
          </span>
          <span className="threads-badge-val">
            {threads} {isEn ? 'threads' : 'потоков'}
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="100"
          value={threads}
          className="threads-range-slider"
          onChange={(e) => setThreads(Number(e.target.value))}
          disabled={isRunning}
        />

        {/* Quick pills 20, 30, 50 */}
        <div className="thread-quick-pills">
          {[20, 30, 50].map((num) => (
            <button
              key={num}
              type="button"
              className={`thread-pill-btn ${threads === num ? 'active' : ''}`}
              onClick={() => setThreads(num)}
              disabled={isRunning}
            >
              {num}
            </button>
          ))}
        </div>
      </div>

      {/* Password Base Custom Dropdown */}
      <div className="field-group" style={{ marginBottom: '12px' }}>
        <label className="field-label">
          {isEn ? 'PASSWORD DATABASE' : 'База паролей'}
        </label>

        <div
          ref={dropdownRef}
          className={`custom-dropdown-wrap ${isDropdownOpen ? 'open' : ''}`}
        >
          <button
            type="button"
            className="custom-dropdown-trigger"
            onClick={() => !isRunning && setIsDropdownOpen(v => !v)}
            disabled={isRunning}
          >
            <div className="dropdown-trigger-content">
              <span className="dropdown-selected-name">
                {isEn ? currentDictObj.titleEn : currentDictObj.titleRu}
              </span>
              {(isEn ? currentDictObj.badgeEn || currentDictObj.badge : currentDictObj.badge) && (
                <span className="dropdown-badge-rec">
                  {isEn ? (currentDictObj.badgeEn || currentDictObj.badge) : currentDictObj.badge}
                </span>
              )}
            </div>
            <svg
              className="dropdown-chevron"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div className="custom-dropdown-menu">
            {allDicts.map((item) => {
              const isSelected = item.id === selectedDict;
              const badgeText = isEn ? (item.badgeEn || item.badge) : item.badge;

              return (
                <div
                  key={item.id || item.titleRu || 'loading'}
                  className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectDict(item)}
                >
                  <div className="dropdown-item-info">
                    <div className="dropdown-item-title-row">
                      <span className="dropdown-item-title">
                        {isEn ? item.titleEn : item.titleRu}
                      </span>
                      {badgeText && (
                        <span className="dropdown-badge-rec">{badgeText}</span>
                      )}
                    </div>
                    <span className="dropdown-item-sub">
                      {isEn ? item.subEn : item.subRu}
                    </span>
                  </div>

                  <span className="dropdown-check-icon">
                    {isSelected && (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Server host mutator toggle */}
      <div className="toggles-group">
        <label className="toggle-row">
          <div className="toggle-label-wrap">
            <span className="toggle-title">
              {isEn ? 'Server Name Mutations' : 'Мутации по названию сервера'}
            </span>
            <span className="toggle-desc">
              {isEn
                ? 'Generate smart wordlist variations (hypixel123, hypixel2026, admin)'
                : 'Генерация вариаций по имени хоста (hypixel123, hypixel2026, admin)'}
            </span>
          </div>
          <div className="apple-switch">
            <input
              type="checkbox"
              checked={isMutator}
              disabled={isRunning}
              onChange={(e) => setIsMutator(e.target.checked)}
            />
            <span className="switch-slider" />
          </div>
        </label>
      </div>

      {/* Main Start / Stop Button */}
      <button
        type="button"
        className={`action-btn-main ${isRunning ? 'running' : ''}`}
        onClick={onToggleAudit}
      >
        <span className="btn-spinner-icon" />
        {isRunning ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" />
          </svg>
        ) : currentStage === 'ACCESS' ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        )}
        <span>
          {isRunning
            ? (isEn ? 'Stop Audit' : 'Остановить аудит')
            : currentStage === 'ACCESS'
              ? (isEn ? 'Audit Complete [ACCESS]' : 'Аудит завершен [ACCESS]')
              : (isEn ? 'Launch RCON Audit' : 'Запустить аудит RCON')}
        </span>
      </button>
    </div>
  );
};

export default RconScannerSettings;
